import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { uploadPhoto } from "./uploadPhoto";

const API_BASE_URL = "https://api.itrustsecure.com";
function getToken() { return localStorage.getItem("itrust_token"); }
function authHeaders() { return { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` }; }

export default function ListingPage({ user, onLogout }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("details");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState("ACTIVE");

  const [items, setItems] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [itemSaving, setItemSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const isMine = listing?.sellerId === user?.id;

  const loadListing = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/listings/${id}`, { headers: authHeaders() });
      const data = await res.json();
      if (res.ok) {
        setListing(data);
        setTitle(data.title || "");
        setDescription(data.description || "");
        setStatus(data.status || "ACTIVE");
        setItems(data.items || []);
      }
    } catch (err) { console.warn(err); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { loadListing(); }, [loadListing]);

  async function handleSaveListing(e) {
    e.preventDefault();
    setMessage(""); setError("");
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/listings/${id}`, {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify({ title, description, status })
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || "Could not update listing"); return; }
      setMessage("Listing updated successfully!");
      loadListing();
    } catch { setError("Something went wrong"); }
    finally { setSaving(false); }
  }

  async function handlePhotoUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const url = await uploadPhoto(file);
      setEditingItem({
        ...editingItem,
        photoUrls: [...(editingItem.photoUrls || []), url]
      });
    } catch { window.alert("Photo upload failed. Please try again."); }
    finally { setUploadingPhoto(false); }
  }

  async function handleSaveItem(e) {
    e.preventDefault();
    setItemSaving(true);
    const validPhotos = (editingItem.photoUrls || []).filter(u => u.trim() !== "");
    try {
      const res = await fetch(`${API_BASE_URL}/listings/items/${editingItem.id}`, {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify({
          title: editingItem.title,
          description: editingItem.description,
          listedUnitPrice: Number(editingItem.listedUnitPrice),
          totalQuantity: Number(editingItem.totalQuantity),
          isNegotiable: editingItem.isNegotiable,
          minimumOfferPrice: editingItem.minimumOfferPrice ? Number(editingItem.minimumOfferPrice) : null,
          photoUrls: validPhotos,
        })
      });
      const data = await res.json();
      if (!res.ok) { window.alert(data.error || "Could not update item"); return; }
      window.alert("Item updated successfully!");
      setEditingItem(null);
      loadListing();
    } catch { window.alert("Something went wrong"); }
    finally { setItemSaving(false); }
  }

  async function handlePauseListing() {
    if (!window.confirm("Pause this listing? Buyers won't be able to see it.")) return;
    const res = await fetch(`${API_BASE_URL}/listings/${id}`, {
      method: "PUT", headers: authHeaders(),
      body: JSON.stringify({ status: "PAUSED" })
    });
    if (res.ok) { setMessage("Listing paused."); loadListing(); }
    else { const d = await res.json(); setError(d.error || "Could not pause listing"); }
  }

  async function handleActivateListing() {
    const res = await fetch(`${API_BASE_URL}/listings/${id}`, {
      method: "PUT", headers: authHeaders(),
      body: JSON.stringify({ status: "ACTIVE" })
    });
    if (res.ok) { setMessage("Listing activated!"); loadListing(); }
    else { const d = await res.json(); setError(d.error || "Could not activate listing"); }
  }

  function removePhotoUrl(index) {
    setEditingItem({
      ...editingItem,
      photoUrls: (editingItem.photoUrls || []).filter((_, i) => i !== index)
    });
  }

  const styles = {
    root: { fontFamily: "'Segoe UI', Arial, sans-serif", background: "#0a0f1e", color: "#f0f4ff", minHeight: "100vh" },
    nav: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 40px", borderBottom: "1px solid rgba(255,255,255,0.08)", position: "sticky", top: 0, background: "rgba(10,15,30,0.95)", backdropFilter: "blur(10px)", zIndex: 100 },
    navLogo: { fontSize: 20, fontWeight: 800, color: "#fff", cursor: "pointer" },
    navLogoAccent: { color: "#3b82f6" },
    navLinks: { display: "flex", gap: 20, alignItems: "center" },
    navLink: { color: "rgba(255,255,255,0.6)", cursor: "pointer", fontSize: 14, fontWeight: 500 },
    navBtn: { background: "transparent", border: "1px solid rgba(255,255,255,0.2)", color: "#fff", padding: "8px 16px", borderRadius: 8, cursor: "pointer", fontSize: 13 },
    main: { maxWidth: 900, margin: "0 auto", padding: "32px 20px" },
    backBtn: { background: "transparent", border: "none", color: "rgba(255,255,255,0.5)", cursor: "pointer", fontSize: 14, marginBottom: 20, padding: 0 },
    titleRow: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24, flexWrap: "wrap", gap: 12 },
    pageTitle: { fontSize: 28, fontWeight: 800 },
    statusBadge: (s) => {
      const colors = { ACTIVE: "#16a34a", PAUSED: "#d97706", SOLD_OUT: "#6b7280", CANCELED: "#dc2626" };
      const color = colors[s] || "#6b7280";
      return { padding: "6px 14px", borderRadius: 999, background: `${color}20`, color, border: `1px solid ${color}40`, fontSize: 13, fontWeight: 700 };
    },
    actionBtns: { display: "flex", gap: 10 },
    pauseBtn: { background: "rgba(217,119,6,0.1)", color: "#d97706", border: "1px solid rgba(217,119,6,0.3)", padding: "8px 16px", borderRadius: 10, cursor: "pointer", fontWeight: 700, fontSize: 13 },
    activateBtn: { background: "rgba(22,163,74,0.1)", color: "#16a34a", border: "1px solid rgba(22,163,74,0.3)", padding: "8px 16px", borderRadius: 10, cursor: "pointer", fontWeight: 700, fontSize: 13 },
    tabs: { display: "flex", gap: 8, marginBottom: 24 },
    tabBtn: (active) => ({ padding: "10px 20px", borderRadius: 10, border: "1px solid", borderColor: active ? "#3b82f6" : "rgba(255,255,255,0.15)", background: active ? "#3b82f6" : "transparent", color: active ? "#fff" : "rgba(255,255,255,0.6)", cursor: "pointer", fontWeight: 700, fontSize: 13 }),
    card: { background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 16, padding: 24, marginBottom: 16 },
    cardTitle: { fontSize: 16, fontWeight: 700, marginBottom: 20 },
    label: { fontSize: 13, fontWeight: 600, color: "rgba(255,255,255,0.7)", marginBottom: 6, display: "block" },
    input: { width: "100%", padding: "12px 14px", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 10, color: "#fff", fontSize: 14, marginBottom: 16, boxSizing: "border-box", outline: "none" },
    textarea: { width: "100%", padding: "12px 14px", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 10, color: "#fff", fontSize: 14, marginBottom: 16, boxSizing: "border-box", minHeight: 100, resize: "vertical", outline: "none" },
    select: { width: "100%", padding: "12px 14px", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 10, color: "#fff", fontSize: 14, marginBottom: 16, boxSizing: "border-box", outline: "none" },
    saveBtn: { background: "#3b82f6", color: "#fff", border: "none", padding: "12px 24px", borderRadius: 10, cursor: "pointer", fontWeight: 700, fontSize: 14, opacity: saving ? 0.5 : 1 },
    successMsg: { background: "rgba(22,163,74,0.1)", border: "1px solid rgba(22,163,74,0.3)", color: "#86efac", padding: "10px 14px", borderRadius: 8, fontSize: 13, marginBottom: 16 },
    errorMsg: { background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", color: "#fca5a5", padding: "10px 14px", borderRadius: 8, fontSize: 13, marginBottom: 16 },
    itemCard: { background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 14, padding: 16, marginBottom: 12 },
    itemHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 },
    itemTitle: { fontSize: 15, fontWeight: 700 },
    itemMeta: { fontSize: 13, color: "rgba(255,255,255,0.5)", marginBottom: 6 },
    itemPrice: { fontSize: 20, fontWeight: 800, color: "#3b82f6", marginBottom: 8 },
    editBtn: { background: "rgba(59,130,246,0.1)", color: "#3b82f6", border: "1px solid rgba(59,130,246,0.3)", padding: "6px 14px", borderRadius: 8, cursor: "pointer", fontWeight: 700, fontSize: 12 },
    metaRow: { display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 4 },
    metaLabel: { color: "rgba(255,255,255,0.4)" },
    checkboxRow: { display: "flex", alignItems: "center", gap: 10, marginBottom: 16 },
    checkbox: { width: 18, height: 18, cursor: "pointer" },
    checkboxLabel: { fontSize: 14, color: "rgba(255,255,255,0.7)" },
    photoGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: 10, marginBottom: 12 },
    photoThumb: { width: "100%", aspectRatio: "1", objectFit: "cover", borderRadius: 10, border: "1px solid rgba(255,255,255,0.1)" },
    photoThumbWrapper: { position: "relative" },
    removePhotoX: { position: "absolute", top: 4, right: 4, background: "rgba(0,0,0,0.7)", color: "#fff", border: "none", borderRadius: "50%", width: 22, height: 22, cursor: "pointer", fontSize: 12, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" },
    uploadBtn: { background: "rgba(59,130,246,0.1)", border: "2px dashed rgba(59,130,246,0.4)", color: "#3b82f6", padding: "16px", borderRadius: 12, cursor: "pointer", fontWeight: 700, fontSize: 14, marginBottom: 16, width: "100%", textAlign: "center" },
    overlay: { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: 20 },
    modal: { background: "#0f1629", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 20, padding: 32, width: "100%", maxWidth: 520, maxHeight: "90vh", overflowY: "auto" },
    modalTitle: { fontSize: 20, fontWeight: 800, marginBottom: 20 },
    modalActions: { display: "flex", gap: 10, marginTop: 8 },
    cancelBtn: { background: "transparent", color: "rgba(255,255,255,0.5)", border: "1px solid rgba(255,255,255,0.15)", padding: "10px 20px", borderRadius: 10, cursor: "pointer", fontWeight: 700, fontSize: 14, flex: 1 },
    modalSaveBtn: { background: "#3b82f6", color: "#fff", border: "none", padding: "10px 20px", borderRadius: 10, cursor: "pointer", fontWeight: 700, fontSize: 14, flex: 1, opacity: itemSaving ? 0.5 : 1 },
  };

  if (loading) return (
    <div style={styles.root}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", color: "rgba(255,255,255,0.5)" }}>Loading...</div>
    </div>
  );

  if (!listing) return (
    <div style={styles.root}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", color: "rgba(255,255,255,0.5)" }}>Listing not found</div>
    </div>
  );

  return (
    <div style={styles.root}>
      <nav style={styles.nav}>
        <div style={styles.navLogo} onClick={() => navigate("/home")}>
          i<span style={styles.navLogoAccent}>Trust Secure</span>
        </div>
        <div style={styles.navLinks}>
          <span style={styles.navLink} onClick={() => navigate("/home")}>Home</span>
          <span style={styles.navLink} onClick={() => navigate("/orders")}>My Orders</span>
          <span style={styles.navLink} onClick={() => navigate("/seller")}>Sell</span>
          <span style={styles.navLink} onClick={() => navigate("/profile")}>Profile</span>
          <button style={styles.navBtn} onClick={onLogout}>Sign Out</button>
        </div>
      </nav>

      {/* Edit Item Modal */}
      {editingItem && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <div style={styles.modalTitle}>Edit Item</div>
            <form onSubmit={handleSaveItem}>
              <label style={styles.label}>Item Title</label>
              <input style={styles.input} value={editingItem.title} onChange={e => setEditingItem({ ...editingItem, title: e.target.value })} required />

              <label style={styles.label}>Description</label>
              <textarea style={styles.textarea} value={editingItem.description || ""} onChange={e => setEditingItem({ ...editingItem, description: e.target.value })} />

              <label style={styles.label}>Price ($)</label>
              <input style={styles.input} type="number" step="0.01" min="0" value={editingItem.listedUnitPrice} onChange={e => setEditingItem({ ...editingItem, listedUnitPrice: e.target.value })} required />

              <label style={styles.label}>Total Quantity</label>
              <input style={styles.input} type="number" min="1" value={editingItem.totalQuantity} onChange={e => setEditingItem({ ...editingItem, totalQuantity: e.target.value })} required />

              <div style={styles.checkboxRow}>
                <input type="checkbox" style={styles.checkbox} checked={editingItem.isNegotiable} onChange={e => setEditingItem({ ...editingItem, isNegotiable: e.target.checked })} />
                <label style={styles.checkboxLabel}>Allow offers / negotiation</label>
              </div>

              {editingItem.isNegotiable && (
                <>
                  <label style={styles.label}>Minimum Offer Price ($)</label>
                  <input style={styles.input} type="number" step="0.01" min="0" value={editingItem.minimumOfferPrice || ""} onChange={e => setEditingItem({ ...editingItem, minimumOfferPrice: e.target.value })} placeholder="Optional" />
                </>
              )}

              <label style={styles.label}>Photos</label>

              {/* Photo Previews */}
              {(editingItem.photoUrls || []).length > 0 && (
                <div style={styles.photoGrid}>
                  {(editingItem.photoUrls || []).map((url, i) => (
                    <div key={i} style={styles.photoThumbWrapper}>
                      <img src={url} alt={`Photo ${i + 1}`} style={styles.photoThumb} onError={e => { e.target.style.display = "none"; }} />
                      <button type="button" style={styles.removePhotoX} onClick={() => removePhotoUrl(i)}>✕</button>
                    </div>
                  ))}
                </div>
              )}

              {/* Upload Button */}
              <label style={{ ...styles.uploadBtn, opacity: uploadingPhoto ? 0.5 : 1 }}>
                {uploadingPhoto ? "⏳ Uploading..." : "📷 Add Photo from Device"}
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={handlePhotoUpload}
                  disabled={uploadingPhoto}
                />
              </label>

              <div style={styles.modalActions}>
                <button type="button" style={styles.cancelBtn} onClick={() => setEditingItem(null)}>Cancel</button>
                <button type="submit" style={styles.modalSaveBtn} disabled={itemSaving || uploadingPhoto}>
                  {itemSaving ? "Saving..." : "Save Item"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div style={styles.main}>
        <button style={styles.backBtn} onClick={() => navigate("/seller")}>← Back to Seller Dashboard</button>

        <div style={styles.titleRow}>
          <div>
            <div style={styles.pageTitle}>{listing.title}</div>
            <div style={{ fontSize: 13, color: "rgba(255,255,255,0.4)", marginTop: 4 }}>
              {listing.items?.length || 0} items · Listed {new Date(listing.createdAt).toLocaleDateString()}
            </div>
          </div>
          <div style={styles.actionBtns}>
            <span style={styles.statusBadge(listing.status)}>{listing.status}</span>
            {isMine && listing.status === "ACTIVE" && (
              <button style={styles.pauseBtn} onClick={handlePauseListing}>⏸ Pause</button>
            )}
            {isMine && listing.status === "PAUSED" && (
              <button style={styles.activateBtn} onClick={handleActivateListing}>▶ Activate</button>
            )}
          </div>
        </div>

        <div style={styles.tabs}>
          <button style={styles.tabBtn(tab === "details")} onClick={() => setTab("details")}>Listing Details</button>
          <button style={styles.tabBtn(tab === "items")} onClick={() => setTab("items")}>Items ({items.length})</button>
          <button style={styles.tabBtn(tab === "stats")} onClick={() => setTab("stats")}>Stats</button>
        </div>

        {tab === "details" && (
          <div style={styles.card}>
            <div style={styles.cardTitle}>Edit Listing</div>
            {message && <div style={styles.successMsg}>{message}</div>}
            {error && <div style={styles.errorMsg}>{error}</div>}
            {isMine ? (
              <form onSubmit={handleSaveListing}>
                <label style={styles.label}>Title</label>
                <input style={styles.input} value={title} onChange={e => setTitle(e.target.value)} required />
                <label style={styles.label}>Description</label>
                <textarea style={styles.textarea} value={description} onChange={e => setDescription(e.target.value)} placeholder="Describe your listing..." />
                <label style={styles.label}>Status</label>
                <select style={styles.select} value={status} onChange={e => setStatus(e.target.value)}>
                  <option value="ACTIVE">Active</option>
                  <option value="PAUSED">Paused</option>
                </select>
                <button type="submit" style={styles.saveBtn} disabled={saving}>
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </form>
            ) : (
              <div>
                <div style={styles.metaRow}><span style={styles.metaLabel}>Title</span><span>{listing.title}</span></div>
                <div style={styles.metaRow}><span style={styles.metaLabel}>Description</span><span>{listing.description || "—"}</span></div>
                <div style={styles.metaRow}><span style={styles.metaLabel}>Status</span><span>{listing.status}</span></div>
                <div style={styles.metaRow}><span style={styles.metaLabel}>Seller</span><span>{listing.seller?.fullName}</span></div>
              </div>
            )}
          </div>
        )}

        {tab === "items" && (
          items.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px 20px", color: "rgba(255,255,255,0.4)" }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>📦</div>
              <div>No items in this listing</div>
            </div>
          ) : items.map(item => (
            <div key={item.id} style={styles.itemCard}>
              {item.photoUrls?.length > 0 && (
                <div style={styles.photoGrid}>
                  {item.photoUrls.map((url, i) => (
                    <img key={i} src={url} alt={`${item.title} ${i + 1}`} style={styles.photoThumb} onError={e => { e.target.style.display = "none"; }} />
                  ))}
                </div>
              )}
              <div style={styles.itemHeader}>
                <div>
                  <div style={styles.itemTitle}>{item.title}</div>
                  {item.description && <div style={styles.itemMeta}>{item.description}</div>}
                </div>
                {isMine && (
                  <button style={styles.editBtn} onClick={() => setEditingItem({ ...item, photoUrls: item.photoUrls || [] })}>✏️ Edit</button>
                )}
              </div>
              <div style={styles.itemPrice}>${Number(item.listedUnitPrice).toFixed(2)}</div>
              <div style={styles.metaRow}><span style={styles.metaLabel}>Total Qty</span><span>{item.totalQuantity}</span></div>
              <div style={styles.metaRow}><span style={styles.metaLabel}>Available</span><span style={{ color: item.availableQuantity > 0 ? "#16a34a" : "#dc2626" }}>{item.availableQuantity}</span></div>
              <div style={styles.metaRow}><span style={styles.metaLabel}>Reserved</span><span>{item.reservedQuantity}</span></div>
              <div style={styles.metaRow}><span style={styles.metaLabel}>Sold</span><span>{item.soldQuantity}</span></div>
              <div style={styles.metaRow}><span style={styles.metaLabel}>Negotiable</span><span>{item.isNegotiable ? "✅ Yes" : "❌ No"}</span></div>
              {item.isNegotiable && item.minimumOfferPrice && (
                <div style={styles.metaRow}><span style={styles.metaLabel}>Min Offer</span><span>${Number(item.minimumOfferPrice).toFixed(2)}</span></div>
              )}
              <div style={styles.metaRow}><span style={styles.metaLabel}>Photos</span><span>{item.photoUrls?.length || 0} uploaded</span></div>
              <div style={styles.metaRow}><span style={styles.metaLabel}>Status</span><span style={{ color: item.status === "AVAILABLE" ? "#16a34a" : "#d97706" }}>{item.status}</span></div>
            </div>
          ))
        )}

        {tab === "stats" && (
          <div style={styles.card}>
            <div style={styles.cardTitle}>Listing Stats</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 16 }}>
              {[
                { label: "Total Items", value: items.length, color: "#3b82f6" },
                { label: "Available", value: items.reduce((s, i) => s + i.availableQuantity, 0), color: "#16a34a" },
                { label: "Reserved", value: items.reduce((s, i) => s + i.reservedQuantity, 0), color: "#d97706" },
                { label: "Sold", value: items.reduce((s, i) => s + i.soldQuantity, 0), color: "#7c3aed" },
                { label: "Total Value", value: `$${items.reduce((s, i) => s + Number(i.listedUnitPrice) * i.totalQuantity, 0).toFixed(2)}`, color: "#3b82f6" },
                { label: "Sold Value", value: `$${items.reduce((s, i) => s + Number(i.listedUnitPrice) * i.soldQuantity, 0).toFixed(2)}`, color: "#16a34a" },
              ].map((stat, i) => (
                <div key={i} style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: 16 }}>
                  <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", fontWeight: 700, textTransform: "uppercase", marginBottom: 8 }}>{stat.label}</div>
                  <div style={{ fontSize: 24, fontWeight: 800, color: stat.color }}>{stat.value}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}